import { useState, useEffect, useCallback } from 'react';
import type { Recipe } from '../types';
import { recipesService } from '../lib/database';
import { getDataSource } from '../lib/config';
import { initialRecipes } from '../data/recipes';

interface UseRecipesReturn {
  recipes: Recipe[];
  loading: boolean;
  error: string | null;
  save: (recipe: Recipe) => Promise<void>;
  refresh: () => Promise<void>;
}

export const useRecipes = (enabled = true): UseRecipesReturn => {
  const [recipes, setRecipes] = useState<Recipe[]>(initialRecipes);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchRecipes = useCallback(async () => {
    if (!enabled) return;
    const dataSource = getDataSource();
    if (dataSource === 'mock') {
      setRecipes(initialRecipes);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await recipesService.getAll();
      setRecipes(data.length ? data : initialRecipes);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load recipes';
      setError(errorMessage);
      console.error('Error fetching recipes:', err);
      setRecipes(initialRecipes);
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    fetchRecipes();
  }, [fetchRecipes]);

  const save = useCallback(async (recipe: Recipe): Promise<void> => {
    const dataSource = getDataSource();
    if (dataSource === 'mock') {
      console.log('Recipe saved (mock):', { menuItemId: recipe.menuItemId, ingredientCount: recipe.ingredients.length });
      setRecipes((prev) => {
        const idx = prev.findIndex((r) => r.menuItemId === recipe.menuItemId);
        if (idx >= 0) {
          const next = [...prev];
          next[idx] = recipe;
          return next;
        }
        return [...prev, recipe];
      });
      return;
    }
    setError(null);
    try {
      await recipesService.save(recipe);
      console.log('Recipe saved:', { menuItemId: recipe.menuItemId, ingredientCount: recipe.ingredients.length });
      setRecipes((prev) => {
        const idx = prev.findIndex((r) => r.menuItemId === recipe.menuItemId);
        if (idx >= 0) {
          const next = [...prev];
          next[idx] = recipe;
          return next;
        }
        return [...prev, recipe];
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to save recipe';
      setError(errorMessage);
      throw err;
    }
  }, []);

  const refresh = useCallback(async () => {
    await fetchRecipes();
  }, [fetchRecipes]);

  return { recipes, loading, error, save, refresh };
};
